import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { teamService } from '@/services/teamService';
import { teamPhotoService } from '@/services/teamPhotoService';
import { Search, Plus, X, Upload, User, Shield, Users, AlertCircle } from 'lucide-react';
import { getPositionsForSport } from '@/data/sportsPositions';

interface User {
  id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  user_type: 'supabase' | 'internal';
}

interface TeamConnection {
  id: string;
  user_id: string;
  connection_type: 'player' | 'coach' | 'support_staff';
  role?: string;
  position?: string;
  user?: User;
}

interface SeparateTeamManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: number;
  teamName: string;
  teamSport?: string;
  onUpdate: () => void;
}

const COACH_ROLES = [
  'head_coach',
  'assistant_coach',
  'goalkeeping_coach',
  'fitness_coach',
  'tactical_coach',
  'youth_coach',
  'volunteer_coach'
];

const SUPPORT_ROLES = [
  'team_manager',
  'physiotherapist',
  'nutritionist',
  'psychologist',
  'equipment_manager',
  'video_analyst',
  'scout',
  'media_manager',
  'support'
];

export const SeparateTeamManagementModal = ({
  isOpen,
  onClose,
  teamId,
  teamName,
  teamSport,
  onUpdate
}: SeparateTeamManagementModalProps) => {
  const [activeTab, setActiveTab] = useState('logo');
  const [searchTerm, setSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Team connections state
  const [players, setPlayers] = useState<TeamConnection[]>([]);
  const [coaches, setCoaches] = useState<TeamConnection[]>([]);
  const [supportStaff, setSupportStaff] = useState<TeamConnection[]>([]);
  
  // Logo upload state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);

  // Get sport-specific positions
  const getSportPositions = (sport?: string): string[] => {
    if (!sport) return ['Player'];
    return getPositionsForSport(sport);
  };

  // Load team connections
  const loadTeamConnections = async () => {
    try {
      setLoading(true);
      
      // Load team players
      const { data: teamPlayers, error: playersError } = await supabase
        .from('team_players')
        .select('*')
        .eq('team_id', teamId);

      if (!playersError && teamPlayers) {
        const playersWithUsers = await Promise.all(
          teamPlayers.map(async (player) => {
            let userData = null;
            
            // Try Supabase users first
            const { data: profile } = await supabase
              .from('profiles')
              .select('id, username, first_name, last_name, role')
              .eq('id', player.user_id)
              .single();
            
            if (profile) {
              userData = { ...profile, user_type: 'supabase' as const };
            } else {
              // Try internal users
              const { data: internalProfile } = await supabase
                .from('internal_user_profiles')
                .select('user_id, username, first_name, last_name, role')
                .eq('user_id', player.user_id)
                .single();
              
              if (internalProfile) {
                userData = { 
                  id: internalProfile.user_id, 
                  username: internalProfile.username,
                  first_name: internalProfile.first_name,
                  last_name: internalProfile.last_name,
                  role: internalProfile.role,
                  user_type: 'internal' as const 
                };
              }
            }
            
            return {
              id: player.user_id + '-player',
              user_id: player.user_id,
              connection_type: 'player' as const,
              position: player.position,
              user: userData
            };
          })
        );
        setPlayers(playersWithUsers);
      }

      // Load team coaches
      const { data: teamCoaches, error: coachesError } = await supabase
        .from('team_coaches')
        .select('*')
        .eq('team_id', teamId);

      if (!coachesError && teamCoaches) {
        const coachesWithUsers = await Promise.all(
          teamCoaches.map(async (coach) => {
            let userData = null;
            
            // Try Supabase users first
            const { data: profile } = await supabase
              .from('profiles')
              .select('id, username, first_name, last_name, role')
              .eq('id', coach.coach_id)
              .single();
            
            if (profile) {
              userData = { ...profile, user_type: 'supabase' as const };
            } else {
              // Try internal users
              const { data: internalProfile } = await supabase
                .from('internal_user_profiles')
                .select('user_id, username, first_name, last_name, role')
                .eq('user_id', coach.coach_id)
                .single();
              
              if (internalProfile) {
                userData = { 
                  id: internalProfile.user_id, 
                  username: internalProfile.username,
                  first_name: internalProfile.first_name,
                  last_name: internalProfile.last_name,
                  role: internalProfile.role,
                  user_type: 'internal' as const 
                };
              }
            }
            
            return {
              id: coach.coach_id + '-coach',
              user_id: coach.coach_id,
              connection_type: 'coach' as const,
              role: coach.role,
              user: userData
            };
          })
        );
        setCoaches(coachesWithUsers);
      }

      // Load support staff from team_support_staff table
      const { data: teamStaff, error: staffError } = await supabase
        .from('team_support_staff')
        .select('*')
        .eq('team_id', teamId);

      if (!staffError && teamStaff) {
        const staffWithUsers = await Promise.all(
          teamStaff.map(async (staff) => {
            let userData = null;
            
            // Try Supabase users first
            const { data: profile } = await supabase
              .from('profiles')
              .select('id, username, first_name, last_name, role')
              .eq('id', staff.staff_id)
              .single();
            
            if (profile) {
              userData = { ...profile, user_type: 'supabase' as const };
            } else {
              // Try internal users
              const { data: internalProfile } = await supabase
                .from('internal_user_profiles')
                .select('user_id, username, first_name, last_name, role')
                .eq('user_id', staff.staff_id)
                .single();
              
              if (internalProfile) {
                userData = { 
                  id: internalProfile.user_id, 
                  username: internalProfile.username,
                  first_name: internalProfile.first_name,
                  last_name: internalProfile.last_name,
                  role: internalProfile.role,
                  user_type: 'internal' as const 
                };
              }
            }
            
            return {
              id: staff.staff_id + '-support',
              user_id: staff.staff_id,
              connection_type: 'support_staff' as const,
              role: staff.role,
              user: userData
            };
          })
        );
        setSupportStaff(staffWithUsers);
      } else {
        setSupportStaff([]);
      }

    } catch (error) {
      console.error('Error loading team connections:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  // Search users
  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      setAvailableUsers([]);
      return;
    }

    try {
      setLoading(true);
      
      // Search Supabase users
      const { data: supabaseUsers, error: supabaseError } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, role')
        .or(`username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .limit(10);

      // Search internal users
      const { data: internalUsers, error: internalError } = await supabase
        .from('internal_user_profiles')
        .select('user_id, username, first_name, last_name, role')
        .or(`username.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .limit(10);

      // Combine results
      const combined: User[] = [
        ...(supabaseUsers || []).map(user => ({
          id: user.id,
          username: user.username,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.role,
          user_type: 'supabase' as const
        })),
        ...(internalUsers || []).map(user => ({
          id: user.user_id,
          username: user.username,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.role,
          user_type: 'internal' as const
        }))
      ];

      // Filter out existing connections
      const existingUserIds = [...players, ...coaches, ...supportStaff].map(c => c.user_id);
      const filtered = combined.filter(user => !existingUserIds.includes(user.id));

      setAvailableUsers(filtered);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setLoading(false);
    }
  };

  // Add connection
  const addConnection = async (userId: string, userType: 'supabase' | 'internal', connectionType: 'player' | 'coach' | 'support_staff') => {
    try {
      if (connectionType === 'player') {
        const { error } = await supabase
          .from('team_players')
          .insert({
            team_id: teamId,
            user_id: userId,
            position: selectedPosition
          });
        
        if (error) throw error;
      } else if (connectionType === 'coach') {
        const { error } = await supabase
          .from('team_coaches')
          .insert({
            team_id: teamId,
            coach_id: userId,
            role: selectedRole || 'coach'
          });
        
        if (error) throw error;
      } else {
        // Support staff - insert into team_support_staff table
        // Get current user ID for added_by
        let currentUserId = null;
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.id) {
          currentUserId = user.id;
        } else {
          const internalSession = localStorage.getItem('internal_user_session');
          if (internalSession) {
            try {
              const parsed = JSON.parse(internalSession);
              currentUserId = parsed.userId || parsed.user_id || parsed.id;
            } catch {
              // Ignore parse errors
            }
          }
        }

        if (!currentUserId) {
          toast.error('Please log in to add support staff');
          return;
        }

        const { error } = await supabase
          .from('team_support_staff')
          .insert({
            team_id: teamId,
            staff_id: userId,
            role: selectedRole || 'support',
            added_by: currentUserId
          });
        
        if (error) throw error;
      }

      toast.success(`${connectionType} added successfully`);
      loadTeamConnections();
      setSearchTerm('');
      setSelectedRole('');
      setSelectedPosition('');
      setAvailableUsers([]);
    } catch (error) {
      console.error('Error adding connection:', error);
      toast.error(`Failed to add ${connectionType}`);
    }
  };

  // Remove connection
  const removeConnection = async (connectionId: string, userId: string, connectionType: string) => {
    try {
      if (connectionType === 'player') {
        const { error } = await supabase
          .from('team_players')
          .delete()
          .eq('team_id', teamId)
          .eq('user_id', userId);
        
        if (error) throw error;
      } else if (connectionType === 'coach') {
        const { error } = await supabase
          .from('team_coaches')
          .delete()
          .eq('team_id', teamId)
          .eq('coach_id', userId);
        
        if (error) throw error;
      } else if (connectionType === 'support_staff') {
        const { error } = await supabase
          .from('team_support_staff')
          .delete()
          .eq('team_id', teamId)
          .eq('staff_id', userId);
        
        if (error) throw error;
      }

      toast.success(`${connectionType.replace('_', ' ')} removed successfully`);
      loadTeamConnections();
    } catch (error) {
      console.error('Error removing connection:', error);
      toast.error(`Failed to remove ${connectionType.replace('_', ' ')}`);
    }
  };

  // Handle logo upload
  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error('Please select a logo file');
      return;
    }

    try {
      setLogoUploading(true);
      const fileName = `team-${teamId}-logo`;
      const result = await teamPhotoService.uploadTeamLogo(logoFile, fileName);
      
      if (result.success && result.url) {
        // Update team logo in database
        const { error } = await supabase
          .from('teams')
          .update({ team_logo: result.url })
          .eq('team_id', teamId);

        if (error) throw error;

        toast.success('Logo uploaded successfully');
        onUpdate();
        setLogoFile(null);
      } else {
        toast.error(result.error || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadTeamConnections();
    }
  }, [isOpen, teamId]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const getUserDisplayName = (user?: User) => {
    if (!user) return 'Unknown User';
    return user.username || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User';
  };

  const renderUserSearchResults = (connectionType: 'player' | 'coach' | 'support_staff') => (
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {availableUsers.map((user) => (
        <div key={user.id} className="flex items-center justify-between p-2 bg-muted rounded-lg">
          <div>
            <span className="font-medium">{getUserDisplayName(user)}</span>
            <span className="text-sm text-muted-foreground ml-2">({user.role})</span>
          </div>
          <Button
            onClick={() => addConnection(user.id, user.user_type, connectionType)}
            size="sm"
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add
          </Button>
        </div>
      ))}
      {availableUsers.length === 0 && searchTerm && (
        <p className="text-muted-foreground text-center py-4">No users found</p>
      )}
    </div>
  );

  const renderConnectionsList = (connections: TeamConnection[], connectionType: string) => (
    <div className="space-y-2">
      {connections.map((connection) => (
        <div key={connection.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <div>
            <span className="font-medium">{getUserDisplayName(connection.user)}</span>
            {connection.position && (
              <span className="text-sm text-muted-foreground ml-2">- {connection.position}</span>
            )}
            {connection.role && (
              <span className="text-sm text-muted-foreground ml-2">- {connection.role}</span>
            )}
          </div>
          <Button
            onClick={() => removeConnection(connection.id, connection.user_id, connectionType)}
            size="sm"
            variant="destructive"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ))}
      {connections.length === 0 && (
        <p className="text-muted-foreground text-center py-8">No {connectionType}s added yet</p>
      )}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground">Manage {teamName}</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="logo" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Logo
            </TabsTrigger>
            <TabsTrigger value="players" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Players
            </TabsTrigger>
            <TabsTrigger value="coaches" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Coaches
            </TabsTrigger>
            <TabsTrigger value="support" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Support
            </TabsTrigger>
          </TabsList>

          {/* Logo Tab */}
          <TabsContent value="logo" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Team Logo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="logo-upload">Upload New Logo</Label>
                  <Input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setLogoFile(file);
                      }
                    }}
                    className="mt-2"
                  />
                </div>
                {logoFile && (
                  <div className="text-sm text-muted-foreground">
                    Selected: {logoFile.name}
                  </div>
                )}
                <Button
                  onClick={handleLogoUpload}
                  disabled={!logoFile || logoUploading}
                  className="w-full"
                >
                  {logoUploading ? 'Uploading...' : 'Upload Logo'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Players Tab */}
          <TabsContent value="players" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Add Players</span>
                  <Button
                    onClick={() => setActiveTab('players')}
                    size="sm"
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="player-search">Search Players</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="player-search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by username or name..."
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="player-position">Position</Label>
                    <Select value={selectedPosition} onValueChange={setSelectedPosition}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent>
                        {getSportPositions(teamSport).map((position) => (
                          <SelectItem key={position} value={position}>
                            {position}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {searchTerm && renderUserSearchResults('player')}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Current Players ({players.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {renderConnectionsList(players, 'player')}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Coaches Tab */}
          <TabsContent value="coaches" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Add Coaches</span>
                  <Button
                    onClick={() => setActiveTab('coaches')}
                    size="sm"
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="coach-search">Search Coaches</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="coach-search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by username or name..."
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="coach-role">Coaching Role</Label>
                    <Select value={selectedRole} onValueChange={setSelectedRole}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select coaching role" />
                      </SelectTrigger>
                      <SelectContent>
                        {COACH_ROLES.map((role) => (
                          <SelectItem key={role} value={role}>
                            {role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {searchTerm && renderUserSearchResults('coach')}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Current Coaches ({coaches.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {renderConnectionsList(coaches, 'coach')}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Add Support Staff</span>
                  <Button
                    onClick={() => setActiveTab('support')}
                    size="sm"
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="support-search" className="text-white">Search Support Staff</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="support-search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by username or name..."
                      className="pl-10 bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                </div>
                
                {searchTerm && renderUserSearchResults('support_staff')}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Current Support Staff ({supportStaff.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {renderConnectionsList(supportStaff, 'support_staff')}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};